/**
 * Typed field rows for the shape editors (no raw JSON): every row is a
 * hook-free component taking `value` + `onChange`, with the doc.rs comment
 * as its tooltip. Optional fields (Rust `Option`) show "—" when unset and a
 * reset button once set; `undefined` from onChange removes the key.
 */

import type { ReactNode } from 'react';

import { ColorField, IconButton, NumberField, PropRow, SelectField, TextField, Toggle, VecField } from '../../ui/controls.js';
import './shapes.css';

export type Obj = Record<string, unknown>;

/** Copy of `obj` with `key` set (or removed for `undefined`). */
export function patch<T extends Obj>(obj: T, key: string, value: unknown): T {
  const next: Obj = { ...obj };
  if (value === undefined) delete next[key];
  else next[key] = value;
  return next as T;
}

export const num = (v: unknown): number | undefined => (typeof v === 'number' && Number.isFinite(v) ? v : undefined);
export const str = (v: unknown): string | undefined => (typeof v === 'string' ? v : undefined);
export const bool = (v: unknown): boolean | undefined => (typeof v === 'boolean' ? v : undefined);
export const obj = (v: unknown): Obj | undefined => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Obj) : undefined);
export const arr = <T = unknown,>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);
export function vec(v: unknown, size: number): number[] | undefined {
  return Array.isArray(v) && v.length >= size && v.slice(0, size).every((n) => typeof n === 'number') ? (v.slice(0, size) as number[]) : undefined;
}

/** Field row API: `onChange(undefined)` unsets an optional field. */
interface RowProps<T> {
  label: string;
  hint?: string;
  value: T | undefined;
  onChange: (value: T | undefined) => void;
}

function Reset({ show, onClick }: { show: boolean; onClick: () => void }) {
  return show ? <IconButton icon="x" title="Reset (use the default)" size={12} className="field-reset" onClick={onClick} /> : null;
}

export function Num({
  label,
  hint,
  value,
  onChange,
  optional = false,
  fallback,
  integer = false,
  step,
  min,
  max,
}: RowProps<number> & {
  /** Unset shows "—" (inherit / default); a reset button clears it. */
  optional?: boolean;
  /** Shown when unset and not optional. */
  fallback?: number;
  integer?: boolean;
  step?: number;
  min?: number;
  max?: number;
}) {
  return (
    <PropRow label={label} hint={hint}>
      <NumberField
        value={value ?? (optional ? undefined : fallback)}
        integer={integer}
        step={step ?? (integer ? 1 : 0.01)}
        min={min}
        max={max}
        title={hint}
        onChange={onChange}
      />
      {optional && <Reset show={value !== undefined} onClick={() => onChange(undefined)} />}
    </PropRow>
  );
}

export function Bool({
  label,
  hint,
  value,
  onChange,
  fallback = false,
  optional = false,
}: RowProps<boolean> & { fallback?: boolean; optional?: boolean }) {
  if (optional) {
    return (
      <PropRow label={label} hint={hint}>
        <SelectField
          value={value === undefined ? '' : value ? 'on' : 'off'}
          options={[
            { value: '', label: 'Default' },
            { value: 'on', label: 'On' },
            { value: 'off', label: 'Off' },
          ]}
          onChange={(v) => onChange(v === '' ? undefined : v === 'on')}
        />
      </PropRow>
    );
  }
  return (
    <PropRow label={label} hint={hint}>
      <Toggle checked={value ?? fallback} onChange={onChange} />
    </PropRow>
  );
}

export function Enum({
  label,
  hint,
  value,
  onChange,
  options,
  unset,
  fallback,
}: RowProps<string> & {
  options: readonly (string | { value: string; label: string })[];
  /** Label of the "not set" choice (e.g. "Preset"); omitted = always set. */
  unset?: string;
  fallback?: string;
}) {
  const all = unset !== undefined ? [{ value: '', label: unset }, ...options] : options;
  return (
    <PropRow label={label} hint={hint}>
      <SelectField
        value={value ?? (unset !== undefined ? '' : fallback)}
        options={all}
        onChange={(v) => onChange(v === '' && unset !== undefined ? undefined : v)}
      />
    </PropRow>
  );
}

export function Text({ label, hint, value, onChange, placeholder }: RowProps<string> & { placeholder?: string }) {
  return (
    <PropRow label={label} hint={hint}>
      <TextField value={value ?? ''} placeholder={placeholder} onChange={(v) => onChange(v === '' ? undefined : v)} />
    </PropRow>
  );
}

export function Vec({
  label,
  hint,
  value,
  onChange,
  size = 3,
  step = 0.01,
  optional = false,
  fallback,
  labels,
}: RowProps<number[]> & {
  size?: 2 | 3;
  step?: number;
  /** Toggle + vector: unset when off. */
  optional?: boolean;
  /** Value shown (and set when switched on) while unset. */
  fallback?: number[];
  labels?: string[];
}) {
  const def = fallback ?? Array.from({ length: size }, () => 0);
  return (
    <PropRow label={label} hint={hint}>
      {optional && <Toggle checked={value !== undefined} onChange={(on) => onChange(on ? (value ?? def) : undefined)} />}
      {(!optional || value !== undefined) && (
        <VecField size={size} step={step} labels={labels} value={value ?? def} onChange={(v) => onChange(v)} />
      )}
    </PropRow>
  );
}

export function Color({
  label,
  hint,
  value,
  onChange,
  optional = true,
  fallback = [1, 1, 1],
}: RowProps<number[]> & { optional?: boolean; fallback?: number[] }) {
  return (
    <PropRow label={label} hint={hint}>
      {optional && <Toggle checked={value !== undefined} onChange={(on) => onChange(on ? (value ?? fallback) : undefined)} />}
      {(!optional || value !== undefined) && <ColorField alpha={false} value={value ?? fallback} onChange={(v) => onChange(v.slice(0, 3))} />}
    </PropRow>
  );
}

/** Select of names (parts, bones, materials); "" = unset. Unknown values stay visible. */
export function Pick({
  label,
  hint,
  value,
  onChange,
  options,
  none = 'None',
}: RowProps<string> & { options: readonly string[]; none?: string }) {
  return (
    <PropRow label={label} hint={hint}>
      <SelectField
        value={value ?? ''}
        options={[{ value: '', label: none }, ...options]}
        onChange={(v) => onChange(v === '' ? undefined : v)}
      />
    </PropRow>
  );
}

/** A list of names picked from `options` (bones, collide parts), as removable chips. */
export function PickList({
  label,
  hint,
  value,
  onChange,
  options,
  placeholder = 'Add…',
}: RowProps<string[]> & { options: readonly string[]; placeholder?: string }) {
  const list = value ?? [];
  const left = options.filter((o) => !list.includes(o));
  return (
    <PropRow label={label} hint={hint}>
      <div className="chip-list">
        {list.map((name, i) => (
          <span key={`${name}${i}`} className="chip">
            <span className="chip-label">{name}</span>
            <button
              type="button"
              className="chip-x"
              aria-label={`Remove ${name}`}
              onClick={() => {
                const next = list.filter((_, j) => j !== i);
                onChange(next.length ? next : undefined);
              }}
            >
              ×
            </button>
          </span>
        ))}
        <SelectField value={undefined} placeholder={placeholder} options={left} onChange={(v) => v && onChange([...list, v])} />
      </div>
    </PropRow>
  );
}

/** Sub-heading inside a card, collapsible (native <details>, no state). */
export function Section({ title, hint, children, open = true }: { title: string; hint?: string; children: ReactNode; open?: boolean }) {
  return (
    <details className="shape-section" open={open}>
      <summary title={hint}>{title}</summary>
      <div className="shape-section-body">{children}</div>
    </details>
  );
}

/**
 * An optional sub-object (`Option<Struct>`): a toggle that creates it from
 * `make()` or removes it, and its fields when present.
 */
export function OptionalSection<T extends Obj>({
  title,
  hint,
  value,
  make,
  onChange,
  children,
}: {
  title: string;
  hint?: string;
  value: T | undefined;
  make: () => T;
  onChange: (value: T | undefined) => void;
  children: (value: T, set: (key: string, v: unknown) => void) => ReactNode;
}) {
  return (
    <div className={`shape-optional${value ? ' is-on' : ''}`}>
      <PropRow label={title} hint={hint}>
        <Toggle checked={value !== undefined} onChange={(on) => onChange(on ? make() : undefined)} />
      </PropRow>
      {value && <div className="shape-optional-body">{children(value, (key, v) => onChange(patch(value, key, v)))}</div>}
    </div>
  );
}

/**
 * Editable list (add / duplicate / remove / reorder); each item collapses.
 * `render(item, set)` edits one item; `set(next)` replaces it.
 */
export function ListEditor<T>({
  title,
  hint,
  items,
  onChange,
  make,
  itemTitle,
  render,
  addLabel = 'Add',
  open = false,
  empty,
}: {
  title: string;
  hint?: string;
  items: readonly T[];
  onChange: (items: T[]) => void;
  make: (items: readonly T[]) => T;
  itemTitle: (item: T, index: number) => string;
  render: (item: T, set: (next: T) => void, index: number) => ReactNode;
  addLabel?: string;
  /** Items start expanded. */
  open?: boolean;
  empty?: string;
}) {
  const move = (i: number, d: -1 | 1) => {
    const next = items.slice();
    const [m] = next.splice(i, 1);
    next.splice(i + d, 0, m!);
    onChange(next);
  };
  return (
    <div className="shape-list">
      <div className="shape-list-head" title={hint}>
        <span className="shape-list-title">{title}</span>
        <span className="shape-list-count">{items.length}</span>
        <IconButton icon="plus" title={addLabel} size={13} onClick={() => onChange([...items, make(items)])} />
      </div>
      {items.length === 0 && empty && <p className="muted card-note">{empty}</p>}
      {items.map((item, i) => (
        <details key={i} className="shape-list-item" open={open}>
          <summary>
            <span className="op-index">{i + 1}</span>
            <span className="shape-list-item-title">{itemTitle(item, i)}</span>
            <span className="shape-list-item-actions" onClick={(e) => e.preventDefault()}>
              <IconButton icon="chevronUp" title="Move up" size={12} disabled={i === 0} onClick={() => move(i, -1)} />
              <IconButton icon="chevronDown" title="Move down" size={12} disabled={i === items.length - 1} onClick={() => move(i, 1)} />
              <IconButton
                icon="copy"
                title="Duplicate"
                size={12}
                onClick={() => {
                  const next = items.slice();
                  next.splice(i + 1, 0, JSON.parse(JSON.stringify(item)) as T);
                  onChange(next);
                }}
              />
              <IconButton icon="trash" title="Remove" size={12} onClick={() => onChange(items.filter((_, j) => j !== i))} />
            </span>
          </summary>
          <div className="shape-list-body">
            {render(
              item,
              (next) => onChange(items.map((x, j) => (j === i ? next : x))),
              i,
            )}
          </div>
        </details>
      ))}
    </div>
  );
}

/** Small integer list (`[3, 5, 2]`): one number field per entry, + / − at the end. */
export function IntList({
  label,
  hint,
  value,
  onChange,
  min = 0,
  make = 1,
  labels,
}: RowProps<number[]> & { min?: number; make?: number; labels?: (i: number) => string }) {
  const list = value ?? [];
  return (
    <PropRow label={label} hint={hint}>
      <div className="int-list">
        {list.map((n, i) => (
          <NumberField
            key={i}
            label={labels?.(i)}
            value={n}
            integer
            step={1}
            min={min}
            onChange={(v) => onChange(list.map((x, j) => (j === i ? v : x)))}
          />
        ))}
        <IconButton icon="plus" title="Add entry" size={12} onClick={() => onChange([...list, list[list.length - 1] ?? make])} />
        {list.length > 0 && <IconButton icon="minus" title="Remove last entry" size={12} onClick={() => onChange(list.slice(0, -1))} />}
      </div>
    </PropRow>
  );
}
